import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from './supabase';
import type {
  Profile,
  StudySession,
  DailyStat,
  Room,
  RoomParticipant,
  Message,
  TimerMode,
  PomodoroPhase,
  Subject,
  Chapter,
  SubjectProgress,
  FocusTask,
  FeedPost,
  FeedComment,
  MediaItem,
  VoiceNote,
  DailyTarget,
  RevisionLogEntry,
  SubjectStreak,
  Recommendation,
  SubjectHealthReport,
} from './types';

const TIMER_STORAGE_KEY = 'focusflow_active_timer_v2';

const DEFAULT_SUBJECTS: Array<Pick<Subject, 'code' | 'exam_type' | 'name' | 'total_chapters'>> = [
  { code: 'UPSC_POLITY', exam_type: 'UPSC', name: 'UPSC Polity', total_chapters: 80 },
  { code: 'UPSC_HISTORY', exam_type: 'UPSC', name: 'UPSC History', total_chapters: 120 },
  { code: 'UPSC_GEO', exam_type: 'UPSC', name: 'UPSC Geography', total_chapters: 90 },
  { code: 'UPSC_ECON', exam_type: 'UPSC', name: 'UPSC Economy', total_chapters: 70 },
  { code: 'SSC_QUANT', exam_type: 'SSC', name: 'SSC Quantitative Aptitude', total_chapters: 60 },
  { code: 'SSC_REASON', exam_type: 'SSC', name: 'SSC Reasoning', total_chapters: 70 },
  { code: 'SSC_ENGLISH', exam_type: 'SSC', name: 'SSC English', total_chapters: 65 },
  { code: 'SSC_GK', exam_type: 'SSC', name: 'SSC General Awareness', total_chapters: 80 },
];

interface PersistedTimerState {
  isRunning: boolean;
  isPaused: boolean;
  elapsedSeconds: number;
  remainingSeconds: number | null;
  mode: TimerMode;
  phase: PomodoroPhase;
  targetMinutes: number | null;
  breakMinutes: number;
  sessionId: string | null;
  startedAt: string | null;
  selectedSubjectId: string | null;
  selectedTaskId: string | null;
  selectedChapterId: string | null;
  isRevision: boolean;
  lastTickAt: number | null;
}

const saveTimerSnapshot = (state: PersistedTimerState) => {
  try {
    localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore localStorage errors
  }
};

const loadTimerSnapshot = (): PersistedTimerState | null => {
  try {
    const raw = localStorage.getItem(TIMER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedTimerState;
  } catch {
    return null;
  }
};

const clearTimerSnapshot = () => {
  try {
    localStorage.removeItem(TIMER_STORAGE_KEY);
  } catch {
    // ignore localStorage errors
  }
};

const addDaysToDateString = (dateString: string, amount: number) => {
  const base = new Date(`${dateString}T09:00:00`);
  base.setDate(base.getDate() + amount);
  return base.toISOString().slice(0, 10);
};

const getNextRecurringDueDate = (task: FocusTask) => {
  if (!task.repeat_config) return null;

  if (task.repeat_config.type === 'daily') {
    return addDaysToDateString(task.due_date, 1);
  }

  if (task.repeat_config.type === 'weekly') {
    return addDaysToDateString(task.due_date, 7);
  }

  if (task.repeat_config.type === 'custom' && task.repeat_config.days?.length) {
    const current = new Date(`${task.due_date}T09:00:00`);
    const sortedDays = [...task.repeat_config.days].sort((a, b) => a - b);

    for (let offset = 1; offset <= 7; offset += 1) {
      const next = new Date(current);
      next.setDate(current.getDate() + offset);
      if (sortedDays.includes(next.getDay())) {
        return next.toISOString().slice(0, 10);
      }
    }
  }

  return null;
};

const shiftReminderToNextDueDate = (task: FocusTask, nextDueDate: string) => {
  if (!task.reminder_at) return null;

  const reminder = new Date(task.reminder_at);
  if (Number.isNaN(reminder.getTime())) return null;

  const nextReminder = new Date(`${nextDueDate}T00:00:00`);
  nextReminder.setHours(reminder.getHours(), reminder.getMinutes(), 0, 0);
  return nextReminder.toISOString();
};

// Auth Store
interface AuthState {
  user: { id: string; email: string } | null;
  profile: Profile | null;
  loading: boolean;
  setUser: (user: { id: string; email: string } | null) => void;
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  signUp: (email: string, password: string, username: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  initialize: () => Promise<() => void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  loading: true,

  setUser: (user) => set({ user }),
  setProfile: (profile) => set({ profile }),
  setLoading: (loading) => set({ loading }),

  signUp: async (email, password, username) => {
    if (!isSupabaseConfigured) return { error: 'Supabase not configured' };
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username } },
    });
    if (error) return { error: error.message };
    if (data.user) {
      set({ user: { id: data.user.id, email: data.user.email! } });
      await get().fetchProfile();
    }
    return { error: null };
  },

  signIn: async (email, password) => {
    if (!isSupabaseConfigured) return { error: 'Supabase not configured' };
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    if (data.user) {
      set({ user: { id: data.user.id, email: data.user.email! } });
      await get().fetchProfile();
    }
    return { error: null };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ user: null, profile: null });
  },

  fetchProfile: async () => {
    const { user } = get();
    if (!user || !isSupabaseConfigured) return;
    const { data } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
    if (data) {
      set({ profile: data as Profile });
      return;
    }

    // Self-heal missing profile rows for older users.
    const fallbackUsername = (user.email || `user_${user.id.slice(0, 8)}`).split('@')[0];
    const { data: inserted } = await supabase
      .from('profiles')
      .upsert(
        {
          id: user.id,
          username: fallbackUsername,
        },
        { onConflict: 'id' }
      )
      .select('*')
      .maybeSingle();

    if (inserted) set({ profile: inserted as Profile });
  },

  initialize: async () => {
    if (!isSupabaseConfigured) {
      set({ loading: false });
      return () => { };
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session?.user) {
      set({ user: { id: session.user.id, email: session.user.email! } });
      await get().fetchProfile();
    }

    set({ loading: false });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
      if (nextSession?.user) {
        set({ user: { id: nextSession.user.id, email: nextSession.user.email! } });
        await get().fetchProfile();
      } else {
        set({ user: null, profile: null });
      }
    });

    return () => subscription.unsubscribe();
  },
}));

// Dashboard Store
interface DashboardState {
  sessions: StudySession[];
  dailyStats: DailyStat[];
  weeklyStats: DailyStat[];
  totalSessions: number;
  loading: boolean;
  fetchDashboard: () => Promise<void>;
  subscribeRealtime: () => () => void;
  applyOptimisticSession: (minutes: number) => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  sessions: [],
  dailyStats: [],
  weeklyStats: [],
  totalSessions: 0,
  loading: true,

  fetchDashboard: async () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) {
      set({ loading: false });
      return;
    }

    const today = new Date();
    const sixtyDaysAgo = new Date(today);
    sixtyDaysAgo.setDate(today.getDate() - 60);

    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const [sessionsRes, totalSessionsRes, dailyRes, weeklyRes] = await Promise.all([
      supabase
        .from('study_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('start_time', { ascending: false })
        .limit(100),
      supabase
        .from('study_sessions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId),
      supabase
        .from('daily_stats')
        .select('*')
        .eq('user_id', userId)
        .gte('date', sixtyDaysAgo.toISOString().split('T')[0])
        .order('date', { ascending: true }),
      supabase
        .from('daily_stats')
        .select('*')
        .eq('user_id', userId)
        .gte('date', sevenDaysAgo.toISOString().split('T')[0])
        .order('date', { ascending: true }),
    ]);

    set({
      sessions: (sessionsRes.data as StudySession[]) || [],
      dailyStats: (dailyRes.data as DailyStat[]) || [],
      weeklyStats: (weeklyRes.data as DailyStat[]) || [],
      totalSessions: totalSessionsRes.count || 0,
      loading: false,
    });
  },

  subscribeRealtime: () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return () => { };

    let debounceTimer: number | null = null;
    const debounceFetch = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        void get().fetchDashboard();
      }, 150);
    };

    const channel = supabase
      .channel(`dashboard-changes-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'study_sessions', filter: `user_id=eq.${userId}` },
        debounceFetch
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'daily_stats', filter: `user_id=eq.${userId}` },
        debounceFetch
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
        () => {
          void useAuthStore.getState().fetchProfile();
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      void supabase.removeChannel(channel);
    };
  },

  applyOptimisticSession: (minutes) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return;
    const today = new Date().toISOString().slice(0, 10);

    set((state) => {
      const dailyStats = [...state.dailyStats];
      const existingIndex = dailyStats.findIndex((d) => d.date === today);
      if (existingIndex >= 0) {
        const stat = dailyStats[existingIndex];
        dailyStats[existingIndex] = {
          ...stat,
          minutes_studied: stat.minutes_studied + minutes,
          sessions_count: stat.sessions_count + 1,
          xp_earned: stat.xp_earned + minutes * 5,
        };
      } else {
        dailyStats.push({
          id: `optimistic-${Date.now()}`,
          user_id: userId,
          date: today,
          minutes_studied: minutes,
          sessions_count: 1,
          xp_earned: minutes * 5,
        });
      }

      const weeklyStats = [...state.weeklyStats];
      const weeklyIndex = weeklyStats.findIndex((d) => d.date === today);
      if (weeklyIndex >= 0) {
        const stat = weeklyStats[weeklyIndex];
        weeklyStats[weeklyIndex] = {
          ...stat,
          minutes_studied: stat.minutes_studied + minutes,
          sessions_count: stat.sessions_count + 1,
          xp_earned: stat.xp_earned + minutes * 5,
        };
      } else {
        weeklyStats.push({
          id: `optimistic-week-${Date.now()}`,
          user_id: userId,
          date: today,
          minutes_studied: minutes,
          sessions_count: 1,
          xp_earned: minutes * 5,
        });
      }

      return {
        dailyStats,
        weeklyStats,
        totalSessions: state.totalSessions + 1,
      };
    });
  },
}));

// Subjects + Tasks Store
interface SubjectsState {
  subjects: Subject[];
  chapters: Chapter[];
  progress: SubjectProgress[];
  tasks: FocusTask[];
  loading: boolean;
  saving: boolean;
  fetchAll: () => Promise<void>;
  addTask: (payload: {
    title: string;
    subjectId: string | null;
    estimatedMinutes: number;
    priority?: 'low' | 'medium' | 'high';
    dueDate?: string;
    calendarSlot?: { start: string; end: string };
    status?: FocusTask['status'];
    notes?: string;
    reminderAt?: string | null;
    repeatConfig?: FocusTask['repeat_config'] | null;
  }) => Promise<void>;
  createSubject: (payload: { name: string; examType: Subject['exam_type']; color: string; targetDate?: string; priority?: string }) => Promise<void>;
  createChapter: (subjectId: string, name: string, targetMinutes?: number) => Promise<void>;
  updateTask: (taskId: string, updates: Partial<FocusTask>) => Promise<void>;
  toggleTaskComplete: (taskId: string, isCompleted: boolean) => Promise<void>;
  subscribeRealtime: () => () => void;
  applySessionImpact: (payload: {
    minutes: number;
    xp: number;
    subjectId?: string | null;
    taskId?: string | null;
  }) => Promise<void>;
  incrementChapter: (subjectId: string, delta: number) => Promise<void>;
  incrementRevision: (subjectId: string) => Promise<void>;
  updateChapterDetails: (chapterId: string, updates: Partial<Chapter>) => Promise<void>;
  markChapterRevision: (chapterId: string) => Promise<void>;
  deleteSubject: (subjectId: string) => Promise<void>;
  deleteChapter: (chapterId: string) => Promise<void>;
  saveAllProgress: () => Promise<void>;
}

export const useSubjectsStore = create<SubjectsState>((set, get) => ({
  subjects: [],
  chapters: [],
  progress: [],
  tasks: [],
  loading: true,
  saving: false,

  fetchAll: async () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) {
      set({ loading: false });
      return;
    }

    const existingSubjects = await supabase.from('subjects').select('id').limit(1);
    if ((existingSubjects.data || []).length === 0) {
      await supabase.from('subjects').insert(DEFAULT_SUBJECTS);
    }

    const today = new Date().toISOString().slice(0, 10);

    const [subjectsRes, progressRes, tasksRes, chaptersRes] = await Promise.all([
      supabase.from('subjects').select('*').order('exam_type').order('name'),
      supabase
        .from('subject_progress')
        .select('*, subjects(*)')
        .eq('user_id', userId),
      supabase
        .from('tasks')
        .select('*, subjects(*)')
        .eq('user_id', userId)
        .gte('due_date', today)
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: false }),
      supabase
        .from('chapters')
        .select('*')
        .order('created_at', { ascending: true }),
    ]);

    const subjectsData = (subjectsRes.data as Subject[]) || [];
    const chaptersData = (chaptersRes.data as Chapter[]) || [];
    const progressData = (progressRes.data as SubjectProgress[]) || [];

    // Ensure each subject has a matching subject_progress row and chapters scaffold.
    await Promise.all(
      subjectsData.map(async (subject) => {
        const hasProgress = progressData.some((p) => p.subject_id === subject.id);
        if (!hasProgress) {
          await supabase.from('subject_progress').upsert(
            { user_id: userId, subject_id: subject.id },
            { onConflict: 'user_id,subject_id' }
          );
        }

        const subjectChapters = chaptersData.filter((c) => c.subject_id === subject.id);
        const toCreate = Math.max(0, subject.total_chapters - subjectChapters.length);
        if (toCreate > 0) {
          const rows = Array.from({ length: toCreate }, (_, i) => ({
            subject_id: subject.id,
            name: `Chapter ${subjectChapters.length + i + 1}`,
          }));
          await supabase.from('chapters').insert(rows);
        }
      })
    );

    // Reload after scaffold sync so UI gets complete data.
    const [progressReload, tasksReload, chaptersReload] = await Promise.all([
      supabase.from('subject_progress').select('*, subjects(*)').eq('user_id', userId),
      supabase
        .from('tasks')
        .select('*, subjects(*)')
        .eq('user_id', userId)
        .gte('due_date', today)
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: false }),
      supabase.from('chapters').select('*').order('created_at', { ascending: true }),
    ]);

    set({
      subjects: subjectsData,
      chapters: (chaptersReload.data as Chapter[]) || chaptersData,
      progress: (progressReload.data as SubjectProgress[]) || progressData,
      tasks: (tasksReload.data as FocusTask[]) || ((tasksRes.data as FocusTask[]) || []),
      loading: false,
    });
  },

  addTask: async ({
    title,
    subjectId,
    estimatedMinutes,
    priority = 'medium',
    dueDate,
    calendarSlot,
    status = 'planned',
    notes,
    reminderAt,
    repeatConfig,
  }) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    await supabase.from('tasks').insert({
      user_id: userId,
      subject_id: subjectId,
      title,
      estimated_minutes: estimatedMinutes,
      priority,
      status,
      due_date: dueDate || new Date().toISOString().slice(0, 10),
      calendar_slot_start: calendarSlot?.start,
      calendar_slot_end: calendarSlot?.end,
      notes: notes || '',
      reminder_at: reminderAt || null,
      repeat_config: repeatConfig || null,
    });
    await get().fetchAll();
  },

  updateTask: async (taskId, updates) => {
    if (!isSupabaseConfigured) return;
    await supabase.from('tasks').update(updates).eq('id', taskId);
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === taskId ? { ...t, ...updates } : t)),
    }));
  },

  toggleTaskComplete: async (taskId, isCompleted) => {
    if (!isSupabaseConfigured) return;
    const task = get().tasks.find((item) => item.id === taskId);
    await supabase
      .from('tasks')
      .update({
        is_completed: isCompleted,
        status: isCompleted ? 'completed' : 'planned',
        completed_at: isCompleted ? new Date().toISOString() : null,
      })
      .eq('id', taskId);

    if (isCompleted && task?.repeat_config) {
      const nextDueDate = getNextRecurringDueDate(task);
      if (nextDueDate) {
        await supabase.from('tasks').insert({
          user_id: task.user_id,
          subject_id: task.subject_id,
          chapter_id: task.chapter_id || null,
          title: task.title,
          is_completed: false,
          status: 'planned',
          priority: task.priority,
          repeat_config: task.repeat_config,
          calendar_slot_start: null,
          calendar_slot_end: null,
          reminder_at: shiftReminderToNextDueDate(task, nextDueDate),
          notes: task.notes || '',
          due_date: nextDueDate,
          estimated_minutes: task.estimated_minutes,
          actual_minutes: 0,
          completed_at: null,
        });
      }
    }

    await get().fetchAll();
  },

  createSubject: async ({ name, examType, color, targetDate, priority = 'medium' }) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    // Check if user already has this subject
    const { data: existing } = await supabase.from('subjects').select('id').eq('name', name).maybeSingle();
    let subjectId = existing?.id;

    if (!subjectId) {
      const { data: newSubject } = await supabase.from('subjects').insert({
        name,
        exam_type: examType,
        code: name.substring(0, 3).toUpperCase(),
        total_chapters: 0,
        created_by: userId,
        color,
        priority,
        target_date: targetDate
      }).select().single();
      subjectId = newSubject?.id;
    }

    if (subjectId) {
      // Init progress
      await supabase.from('subject_progress').insert({
        user_id: userId,
        subject_id: subjectId,
        chapters_completed: 0,
        revision_count: 0,
        minutes_studied: 0,
        xp_earned: 0,
      });
      await get().fetchAll();
    }
  },

  createChapter: async (subjectId, name, targetMinutes = 60) => {
    const { data } = await supabase.from('chapters').insert({
      subject_id: subjectId,
      name,
      is_completed: false,
      revision_count: 0,
      total_minutes: 0,
      target_minutes: targetMinutes
    }).select().single();

    if (data) {
      set(state => ({
        chapters: [...state.chapters, data as Chapter]
      }));

      // Update total_chapters in subject
      const subject = get().subjects.find(s => s.id === subjectId);
      if (subject) {
        const newTotal = (subject.total_chapters || 0) + 1;
        await supabase.from('subjects').update({ total_chapters: newTotal }).eq('id', subjectId);
        set(state => ({
          subjects: state.subjects.map(s => s.id === subjectId ? { ...s, total_chapters: newTotal } : s)
        }));
      }
    }
  },

  deleteChapter: async (chapterId: string) => {
    const chapter = get().chapters.find(c => c.id === chapterId);
    if (!chapter) return;

    const { error } = await supabase.from('chapters').delete().eq('id', chapterId);
    if (!error) {
      set(state => ({
        chapters: state.chapters.filter(c => c.id !== chapterId)
      }));

      // Update total_chapters in subject
      const subject = get().subjects.find(s => s.id === chapter.subject_id);
      if (subject) {
        const newTotal = Math.max(0, (subject.total_chapters || 0) - 1);
        await supabase.from('subjects').update({ total_chapters: newTotal }).eq('id', chapter.subject_id);
        set(state => ({
          subjects: state.subjects.map(s => s.id === chapter.subject_id ? { ...s, total_chapters: newTotal } : s)
        }));
      }
    }
  },

  subscribeRealtime: () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return () => { };

    const channel = supabase
      .channel(`subjects-realtime-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'subject_progress', filter: `user_id=eq.${userId}` },
        () => {
          void get().fetchAll();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks', filter: `user_id=eq.${userId}` },
        () => {
          void get().fetchAll();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chapters' },
        () => {
          void get().fetchAll();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  },

  applySessionImpact: async ({ minutes, xp, subjectId, taskId }) => {
    if (!isSupabaseConfigured) return;

    if (subjectId) {
      const userId = useAuthStore.getState().user?.id;
      if (userId) {
        const existing = get().progress.find((p) => p.subject_id === subjectId);
        if (existing) {
          await supabase
            .from('subject_progress')
            .update({
              minutes_studied: existing.minutes_studied + minutes,
              xp_earned: existing.xp_earned + xp,
              last_studied_at: new Date().toISOString(),
            })
            .eq('id', existing.id);
        } else {
          await supabase.from('subject_progress').insert({
            user_id: userId,
            subject_id: subjectId,
            minutes_studied: minutes,
            xp_earned: xp,
            last_studied_at: new Date().toISOString(),
          });
        }
      }
    }

    if (taskId) {
      const task = get().tasks.find((t) => t.id === taskId);
      if (task) {
        const newActual = task.actual_minutes + minutes;
        const isCompleted = task.is_completed || newActual >= task.estimated_minutes;
        await supabase
          .from('tasks')
          .update({
            actual_minutes: newActual,
            is_completed: isCompleted,
            completed_at: isCompleted ? new Date().toISOString() : task.completed_at,
          })
          .eq('id', taskId);
      }
    }

    await get().fetchAll();
  },

  incrementChapter: async (subjectId, delta) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;

    const subject = get().subjects.find((s) => s.id === subjectId);
    if (!subject) return;

    const subjectChapters = get().chapters
      .filter((c) => c.subject_id === subjectId)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const maxChapters = subject.total_chapters;
    const current = subjectChapters.filter((c) => c.is_completed).length;
    const updated = Math.max(0, Math.min(maxChapters, current + delta));

    const toComplete = subjectChapters.slice(0, updated).map((c) => c.id);
    const toUncomplete = subjectChapters.slice(updated).map((c) => c.id);

    if (toComplete.length > 0) {
      await supabase.from('chapters').update({ is_completed: true }).in('id', toComplete);
    }
    if (toUncomplete.length > 0) {
      await supabase.from('chapters').update({ is_completed: false }).in('id', toUncomplete);
    }

    try {
      await supabase.rpc('recalculate_subject_progress', { p_user: userId });
    } catch {
      // Ignore if RPC missing
    }
    await get().fetchAll();
  },

  incrementRevision: async (subjectId) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;

    const completedChapter = get().chapters
      .filter((c) => c.subject_id === subjectId && c.is_completed)
      .sort((a, b) => b.revision_count - a.revision_count)[0];

    if (completedChapter) {
      await supabase
        .from('chapters')
        .update({ revision_count: completedChapter.revision_count + 1 })
        .eq('id', completedChapter.id);
    } else {
      // No completed chapter yet: apply revision bonus at subject level.
      const sp = get().progress.find((p) => p.subject_id === subjectId);
      const updated = (sp?.revision_count || 0) + 1;
      if (sp) {
        await supabase
          .from('subject_progress')
          .update({ revision_count: updated, last_studied_at: new Date().toISOString() })
          .eq('id', sp.id);
      } else {
        await supabase.from('subject_progress').insert({
          user_id: userId,
          subject_id: subjectId,
          revision_count: updated,
          last_studied_at: new Date().toISOString(),
        });
      }
    }

    try {
      await supabase.rpc('recalculate_subject_progress', { p_user: userId });
    } catch {
      // Ignore if RPC missing
    }
    await get().fetchAll();
  },

  updateChapterDetails: async (chapterId, updates) => {
    await supabase.from('chapters').update(updates).eq('id', chapterId);
    set(state => ({
      chapters: state.chapters.map(c => c.id === chapterId ? { ...c, ...updates } : c)
    }));
  },

  markChapterRevision: async (chapterId) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return;

    // Call RPC which handles spacing logic
    const { data, error } = await supabase.rpc('handle_chapter_revision', {
      p_chapter_id: chapterId,
    });

    if (!error && data) {
      // Trigger notification
      useNotificationStore.getState().addNotification({
        type: 'revision',
        title: 'Revision Scheduled',
        message: `Next revision set for ${new Date(data.next_revision_date).toLocaleDateString()}`,
      });

      // Optimistic update with returned data
      const nextDate = data.next_revision_date;
      set(state => ({
        chapters: state.chapters.map(c => c.id === chapterId ? {
          ...c,
          revision_count: c.revision_count + 1,
          next_revision_date: nextDate,
          revision_history: [...(c.revision_history || []), {
            revised_at: new Date().toISOString(),
            revision_number: c.revision_count + 1,
            next_due: nextDate
          }]
        } : c)
      }));
    } else {
      // Fallback if RPC fails or returns no data (should unlikely happen if RPC is correct)
      await get().fetchAll();
    }
  },

  deleteSubject: async (subjectId) => {
    // Soft delete
    await supabase.from('subjects').update({ is_deleted: true }).eq('id', subjectId);
    // Update local state
    set(state => ({
      subjects: state.subjects.filter(s => s.id !== subjectId)
    }));
  },

  saveAllProgress: async () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;

    set({ saving: true });
    try {
      const progressItems = get().progress;
      await Promise.all(
        progressItems.map((p) =>
          supabase.rpc('save_subject_progress', {
            p_user_id: userId,
            p_subject_id: p.subject_id,
            p_chapters_completed: p.chapters_completed,
            p_revision_count: p.revision_count,
            p_notes: p.notes || '',
          }).then(() => { /* ok */ }, () => { /* Ignore if RPC not deployed yet */ })
        )
      );
      try {
        await supabase.rpc('recalculate_subject_progress', { p_user: userId });
      } catch {
        // Ignore if RPC missing
      }
      await get().fetchAll();
    } finally {
      set({ saving: false });
    }
  },
}));

// Timer Store
interface TimerState {
  isRunning: boolean;
  isPaused: boolean;
  elapsedSeconds: number;
  remainingSeconds: number | null;
  mode: TimerMode;
  phase: PomodoroPhase;
  targetMinutes: number | null;
  breakMinutes: number;
  sessionId: string | null;
  intervalRef: number | null;
  startedAt: string | null;
  selectedSubjectId: string | null;
  selectedTaskId: string | null;
  selectedChapterId: string | null;
  isRevision: boolean;
  lastTickAt: number | null;
  setMode: (mode: TimerMode) => void;
  setTimerConfig: (payload: { targetMinutes: number | null; breakMinutes?: number }) => void;
  setSessionContext: (payload: { subjectId: string | null; taskId: string | null; chapterId?: string | null; isRevision?: boolean }) => void;
  hydrateTimer: () => void;
  startTimer: () => Promise<void>;
  pauseTimer: () => void;
  resumeTimer: () => void;
  stopTimer: () => Promise<{ minutes: number; xp: number } | null>;
  tick: () => void;
  reset: () => void;
}

// Notification Store
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'revision';
  title: string;
  message: string;
  timestamp: string;
}

interface NotificationState {
  notifications: Notification[];
  alarmTriggered: boolean;
  addNotification: (payload: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  triggerAlarm: (triggered: boolean) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  alarmTriggered: false,
  addNotification: (payload) => {
    const id = Math.random().toString(36).substring(7);
    const notification: Notification = {
      ...payload,
      id,
      timestamp: new Date().toISOString(),
    };
    set((state) => ({
      notifications: [notification, ...state.notifications].slice(0, 5), // Keep last 5
    }));
    // Auto remove after 5 seconds
    setTimeout(() => {
      set((state) => ({
        notifications: state.notifications.filter((n) => n.id !== id),
      }));
    }, 5000);
  },
  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },
  triggerAlarm: (triggered) => set({ alarmTriggered: triggered }),
  clearAll: () => set({ notifications: [] }),
}));

const timerPersist = (state: TimerState) => {
  saveTimerSnapshot({
    isRunning: state.isRunning,
    isPaused: state.isPaused,
    elapsedSeconds: state.elapsedSeconds,
    remainingSeconds: state.remainingSeconds,
    mode: state.mode,
    phase: state.phase,
    targetMinutes: state.targetMinutes,
    breakMinutes: state.breakMinutes,
    sessionId: state.sessionId,
    startedAt: state.startedAt,
    selectedSubjectId: state.selectedSubjectId,
    selectedTaskId: state.selectedTaskId,
    selectedChapterId: state.selectedChapterId,
    isRevision: state.isRevision,
    lastTickAt: state.lastTickAt,
  });
};

export const useTimerStore = create<TimerState>((set, get) => ({
  isRunning: false,
  isPaused: false,
  elapsedSeconds: 0,
  remainingSeconds: 1500,
  mode: 'pomodoro',
  phase: 'work',
  targetMinutes: 25,
  breakMinutes: 5,
  sessionId: null,
  intervalRef: null,
  startedAt: null,
  selectedSubjectId: null,
  selectedTaskId: null,
  selectedChapterId: null,
  isRevision: false,
  lastTickAt: null,

  setMode: (mode) => {
    set((state) => {
      const next = {
        ...state,
        mode,
        phase: 'work' as PomodoroPhase,
        remainingSeconds:
          mode === 'count_up'
            ? null
            : Math.max(1, Math.floor((state.targetMinutes || 25) * 60)),
      };
      timerPersist(next as TimerState);
      return next;
    });
  },

  setTimerConfig: ({ targetMinutes, breakMinutes }) => {
    set((state) => {
      const safeTarget = targetMinutes === null ? null : Math.max(1, targetMinutes);
      const next = {
        ...state,
        targetMinutes: safeTarget,
        breakMinutes: breakMinutes ?? state.breakMinutes,
        remainingSeconds:
          state.mode === 'count_up'
            ? null
            : Math.max(1, Math.floor((safeTarget || 25) * 60)),
      };
      timerPersist(next as TimerState);
      return next;
    });
  },

  setSessionContext: ({ subjectId, taskId, chapterId, isRevision }) => {
    set((state) => {
      const next = {
        ...state,
        selectedSubjectId: subjectId,
        selectedTaskId: taskId,
        selectedChapterId: chapterId ?? state.selectedChapterId,
        isRevision: isRevision ?? state.isRevision
      };
      timerPersist(next as TimerState);
      return next;
    });
  },

  hydrateTimer: () => {
    const persisted = loadTimerSnapshot();
    if (!persisted) return;

    const { intervalRef } = get();
    if (intervalRef) window.clearInterval(intervalRef);

    // Never auto-run timer on page open/navigation.
    // Persisted active sessions are restored in paused state, so user must explicitly Resume/Start.
    const shouldRestorePaused = persisted.isRunning || persisted.isPaused;
    set((state) => ({
      ...state,
      ...persisted,
      isRunning: false,
      isPaused: shouldRestorePaused,
      intervalRef: null,
      lastTickAt: null,
    }));
  },

  startTimer: async () => {
    const userId = useAuthStore.getState().user?.id;
    let sessionId: string | null = null;

    if (userId && isSupabaseConfigured) {
      try {
        // Try with all columns first (after migration_fix_dataflow.sql)
        const { data, error } = await supabase
          .from('study_sessions')
          .insert({
            user_id: userId,
            start_time: new Date().toISOString(),
            duration_minutes: 0,
            mode: get().mode,
            target_minutes: get().targetMinutes,
            subject_id: get().selectedSubjectId,
            task_id: get().selectedTaskId,
            chapter_id: get().selectedChapterId,
            is_revision: get().isRevision,
          })
          .select('id')
          .single();

        if (error) {
          // Fallback: columns don't exist yet — insert with core columns only
          const { data: fallbackData } = await supabase
            .from('study_sessions')
            .insert({
              user_id: userId,
              start_time: new Date().toISOString(),
              duration_minutes: 0,
            })
            .select('id')
            .single();
          sessionId = fallbackData?.id || null;
        } else {
          sessionId = data?.id || null;
        }
      } catch {
        // Insert failed entirely — timer still works locally
      }
    }

    const { intervalRef } = get();
    if (intervalRef) window.clearInterval(intervalRef);

    const interval = window.setInterval(() => get().tick(), 1000);
    set((state) => {
      const next = {
        ...state,
        isRunning: true,
        isPaused: false,
        elapsedSeconds: 0,
        remainingSeconds:
          state.mode === 'count_up'
            ? null
            : Math.max(1, Math.floor((state.targetMinutes || 25) * 60)),
        phase: 'work' as PomodoroPhase,
        sessionId,
        intervalRef: interval,
        startedAt: new Date().toISOString(),
        lastTickAt: Date.now(),
      };
      timerPersist(next as TimerState);
      return next;
    });
  },

  pauseTimer: () => {
    const { intervalRef } = get();
    if (intervalRef) window.clearInterval(intervalRef);
    set((state) => {
      const next = { ...state, isPaused: true, isRunning: false, intervalRef: null, lastTickAt: null };
      timerPersist(next as TimerState);
      return next;
    });
  },

  resumeTimer: () => {
    const { intervalRef } = get();
    if (intervalRef) window.clearInterval(intervalRef);
    const interval = window.setInterval(() => get().tick(), 1000);
    set((state) => {
      const next = { ...state, isRunning: true, isPaused: false, intervalRef: interval, lastTickAt: Date.now() };
      timerPersist(next as TimerState);
      return next;
    });
  },

  stopTimer: async () => {
    const { intervalRef, elapsedSeconds, sessionId, selectedSubjectId, selectedTaskId } = get();
    if (intervalRef) window.clearInterval(intervalRef);

    const minutes = Math.max(1, Math.floor(elapsedSeconds / 60));
    const xp = minutes * 5;

    if (isSupabaseConfigured) {
      let rpcSucceeded = false;

      if (sessionId) {
        try {
          // Core RPC: finalizes session, upserts daily_stats, updates profile XP/streak/level
          const { error: rpcError } = await supabase.rpc('complete_study_session', {
            p_session_id: sessionId,
            p_duration_minutes: minutes,
          });

          if (rpcError) {
            console.error('complete_study_session RPC error:', rpcError);
          } else {
            rpcSucceeded = true;
          }
        } catch (e) {
          console.error('complete_study_session RPC threw:', e);
        }
      }

      if (!rpcSucceeded) {
        // FALLBACK: manually update session + daily_stats + profile
        if (sessionId) {
          console.warn('RPC failed, using manual fallback');
        } else {
          // No session ID means startTimer failed (e.g. migration missing),
          // so we just do a local/manual update without warning excessively.
          console.log('No sessionId available, using manual stats update');
        }
        const userId = useAuthStore.getState().user?.id;
        if (userId) {
          try {
            // Update session end time if we have one
            if (sessionId) {
              await supabase
                .from('study_sessions')
                .update({ end_time: new Date().toISOString(), duration_minutes: minutes })
                .eq('id', sessionId);
            }

            // Upsert daily stats
            const todayStr = new Date().toISOString().split('T')[0];
            const { data: existingStat } = await supabase
              .from('daily_stats')
              .select('*')
              .eq('user_id', userId)
              .eq('date', todayStr)
              .maybeSingle();

            if (existingStat) {
              await supabase
                .from('daily_stats')
                .update({
                  minutes_studied: existingStat.minutes_studied + minutes,
                  sessions_count: existingStat.sessions_count + 1,
                  xp_earned: existingStat.xp_earned + xp,
                })
                .eq('id', existingStat.id);
            } else {
              const { error: insertErr } = await supabase.from('daily_stats').insert({
                user_id: userId,
                date: todayStr,
                minutes_studied: minutes,
                sessions_count: 1,
                xp_earned: xp,
              });

              // Conflict can happen due to race; retry with direct update.
              if (insertErr) {
                const { data: racedStat } = await supabase
                  .from('daily_stats')
                  .select('*')
                  .eq('user_id', userId)
                  .eq('date', todayStr)
                  .maybeSingle();

                if (racedStat) {
                  await supabase
                    .from('daily_stats')
                    .update({
                      minutes_studied: racedStat.minutes_studied + minutes,
                      sessions_count: racedStat.sessions_count + 1,
                      xp_earned: racedStat.xp_earned + xp,
                    })
                    .eq('id', racedStat.id);
                }
              }
            }

            // Update profile
            const { data: currentProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', userId)
              .maybeSingle();

            if (currentProfile) {
              const today = todayStr;
              const lastDate = currentProfile.last_study_date;
              let newStreak = 1;
              if (lastDate === today) {
                newStreak = currentProfile.streak;
              } else if (lastDate) {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                if (lastDate === yesterday.toISOString().split('T')[0]) {
                  newStreak = currentProfile.streak + 1;
                }
              }
              const newXp = currentProfile.xp + xp;
              const newLevel = Math.max(1, Math.floor(Math.sqrt(newXp / 100)) + 1);

              await supabase
                .from('profiles')
                .update({
                  xp: newXp,
                  total_focus_minutes: currentProfile.total_focus_minutes + minutes,
                  streak: newStreak,
                  level: newLevel,
                  last_study_date: today,
                })
                .eq('id', userId);
            }
          } catch (fallbackErr) {
            console.error('Manual fallback failed:', fallbackErr);
          }
        }
      }

      // Optimistic update so UI reacts instantly
      useDashboardStore.getState().applyOptimisticSession(minutes);

      // Re-fetch real data from DB (profile, dashboard, subjects) in parallel
      await Promise.all([
        useAuthStore.getState().fetchProfile(),
        useDashboardStore.getState().fetchDashboard(),
        useSubjectsStore.getState().applySessionImpact({
          minutes,
          xp,
          subjectId: selectedSubjectId,
          taskId: selectedTaskId,
        }),
      ]);

      // Update subject streak & daily target in background
      const userId = useAuthStore.getState().user?.id;
      if (userId) {
        if (selectedSubjectId) {
          supabase.rpc('update_subject_streak', { p_user_id: userId, p_subject_id: selectedSubjectId }).then(() => { }, () => { });
        }
        supabase.rpc('update_daily_target_achieved', { p_user_id: userId }).then(() => { }, () => { });
        supabase.rpc('recalculate_subject_progress', { p_user: userId }).then(() => { }, () => { });
      }
    }

    set((state) => ({
      ...state,
      isRunning: false,
      isPaused: false,
      sessionId: null,
      intervalRef: null,
      startedAt: null,
      lastTickAt: null,
    }));

    // Persist a clean idle snapshot so reload/navigation doesn't resurrect
    // a completed session as a fake paused timer.
    saveTimerSnapshot({
      isRunning: false,
      isPaused: false,
      elapsedSeconds: 0,
      remainingSeconds:
        get().mode === 'count_up'
          ? null
          : Math.max(1, Math.floor((get().targetMinutes || 25) * 60)),
      mode: get().mode,
      phase: 'work',
      targetMinutes: get().targetMinutes,
      breakMinutes: get().breakMinutes,
      sessionId: null,
      startedAt: null,
      selectedSubjectId: get().selectedSubjectId,
      selectedTaskId: get().selectedTaskId,
      selectedChapterId: get().selectedChapterId,
      isRevision: get().isRevision,
      lastTickAt: null,
    });
    return { minutes, xp };
  },

  tick: () => {
    const state = get();
    if (!state.lastTickAt || !state.isRunning || state.isPaused) return;

    const now = Date.now();
    const deltaMs = now - state.lastTickAt;
    if (deltaMs < 1000) return; // Only tick if at least 1s passed

    const deltaSecs = Math.floor(deltaMs / 1000);
    let elapsedSeconds = state.elapsedSeconds;
    let remainingSeconds = state.remainingSeconds;
    let phase = state.phase;

    if (state.mode === 'count_up') {
      elapsedSeconds += deltaSecs;
    } else if (state.mode === 'countdown') {
      elapsedSeconds += deltaSecs;
      remainingSeconds = Math.max(0, (remainingSeconds || 0) - deltaSecs);

      // 3-second alarm logic
      if (remainingSeconds <= 3 && remainingSeconds > 0) {
        // We could trigger a repetitive notification or sound here
        // For now, let's just use the notification store if not already sent
      }

      if (remainingSeconds <= 0) {
        void state.stopTimer();
        useNotificationStore.getState().triggerAlarm(true);
        useNotificationStore.getState().addNotification({
          type: 'success',
          title: 'Focus session complete!',
          message: 'Great work! Time for a short break.',
        });
        return;
      }
    } else {
      // Pomodoro
      remainingSeconds = Math.max(0, (remainingSeconds || 0) - deltaSecs);
      if (phase === 'work') {
        elapsedSeconds += deltaSecs;
      }

      const isAboutToSwitch = remainingSeconds <= 3 && remainingSeconds > 0;
      if (isAboutToSwitch) {
        // Trigger pre-end alarm
      }

      if (remainingSeconds <= 0) {
        if (phase === 'work') {
          phase = 'break';
          remainingSeconds = Math.max(1, Math.floor(state.breakMinutes * 60));
          useNotificationStore.getState().addNotification({
            type: 'info',
            title: 'Break Time!',
            message: `Take a ${state.breakMinutes} minute break.`,
          });
        } else {
          phase = 'work';
          remainingSeconds = Math.max(1, Math.floor((state.targetMinutes || 25) * 60));
          useNotificationStore.getState().triggerAlarm(true);
          useNotificationStore.getState().addNotification({
            type: 'revision',
            title: 'Break Over!',
            message: 'Ready to study again?',
          });
        }
      }
    }

    set((prev) => {
      const next = { ...prev, elapsedSeconds, remainingSeconds, phase, lastTickAt: now };
      timerPersist(next as TimerState);
      return next;
    });

    if (state.sessionId && isSupabaseConfigured && elapsedSeconds > 0 && Math.floor(elapsedSeconds / 60) > Math.floor(state.elapsedSeconds / 60)) {
      const roundedMinutes = Math.floor(elapsedSeconds / 60);
      void supabase
        .rpc('heartbeat_study_session', {
          p_session_id: state.sessionId,
          p_duration_minutes: roundedMinutes,
        })
        .then(() => { }, (err) => console.error('Heartbeat failed:', err));
    }
  },

  reset: () => {
    const { intervalRef } = get();
    if (intervalRef) window.clearInterval(intervalRef);
    clearTimerSnapshot();
    set((state) => ({
      ...state,
      isRunning: false,
      isPaused: false,
      elapsedSeconds: 0,
      remainingSeconds:
        state.mode === 'count_up'
          ? null
          : Math.max(1, Math.floor((state.targetMinutes || 25) * 60)),
      phase: 'work',
      sessionId: null,
      intervalRef: null,
      startedAt: null,
      lastTickAt: null,
    }));
  },
}));

// Rooms Store
interface RoomsState {
  rooms: Room[];
  currentRoom: Room | null;
  participants: RoomParticipant[];
  messages: Message[];
  loading: boolean;
  fetchRooms: () => Promise<void>;
  createRoom: (name: string, description?: string, subjectId?: string | null) => Promise<Room | null>;
  joinRoom: (roomId: string) => Promise<void>;
  leaveRoom: (roomId: string) => Promise<void>;
  fetchRoomDetails: (roomId: string) => Promise<void>;
  sendMessage: (roomId: string, message: string) => Promise<void>;
  setStudying: (roomId: string, isStudying: boolean) => Promise<void>;
  subscribeRoom: (roomId: string) => () => void;
}

export const useRoomsStore = create<RoomsState>((set, get) => ({
  rooms: [],
  currentRoom: null,
  participants: [],
  messages: [],
  loading: true,

  fetchRooms: async () => {
    if (!isSupabaseConfigured) {
      set({ loading: false });
      return;
    }

    const [{ data: roomsData }, { data: participantRows }] = await Promise.all([
      supabase
        .from('rooms')
        .select('*, subjects(*)')
        .eq('is_active', true)
        .order('created_at', { ascending: false }),
      supabase.from('room_participants').select('room_id, is_studying'),
    ]);

    const participantMap = new Map<string, { total: number; studying: number }>();
    for (const row of (participantRows || []) as Array<{ room_id: string; is_studying: boolean }>) {
      const existing = participantMap.get(row.room_id) || { total: 0, studying: 0 };
      existing.total += 1;
      if (row.is_studying) existing.studying += 1;
      participantMap.set(row.room_id, existing);
    }

    const hydratedRooms = ((roomsData as Room[]) || []).map((room) => {
      const counts = participantMap.get(room.id);
      return {
        ...room,
        participant_count: counts?.total || 0,
        studying_count: counts?.studying || 0,
      };
    });

    set({ rooms: hydratedRooms, loading: false });
  },

  createRoom: async (name, description, subjectId) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return null;

    const { data } = await supabase
      .from('rooms')
      .insert({ name, description, created_by: userId, subject_id: subjectId || null })
      .select('*, subjects(*)')
      .single();

    if (data) {
      await get().fetchRooms();
      return data as Room;
    }

    return null;
  },

  joinRoom: async (roomId) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;

    await supabase
      .from('room_participants')
      .upsert({ room_id: roomId, user_id: userId }, { onConflict: 'room_id,user_id' });
  },

  leaveRoom: async (roomId) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;

    await supabase.from('room_participants').delete().eq('room_id', roomId).eq('user_id', userId);
    set({ currentRoom: null, participants: [], messages: [] });
  },

  fetchRoomDetails: async (roomId) => {
    if (!isSupabaseConfigured) return;

    const [roomRes, participantsRes, messagesRes] = await Promise.all([
      supabase.from('rooms').select('*, subjects(*)').eq('id', roomId).single(),
      supabase.from('room_participants').select('*, profiles(*)').eq('room_id', roomId),
      supabase
        .from('messages')
        .select('*, profiles(*)')
        .eq('room_id', roomId)
        .order('created_at', { ascending: true })
        .limit(100),
    ]);

    set({
      currentRoom: roomRes.data
        ? {
            ...(roomRes.data as Room),
            participant_count: (participantsRes.data as RoomParticipant[] | null)?.length || 0,
            studying_count:
              ((participantsRes.data as RoomParticipant[] | null) || []).filter((participant) => participant.is_studying).length,
          }
        : null,
      participants: (participantsRes.data as RoomParticipant[]) || [],
      messages: (messagesRes.data as Message[]) || [],
    });
  },

  sendMessage: async (roomId, message) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    await supabase.from('messages').insert({ room_id: roomId, user_id: userId, message });
  },

  setStudying: async (roomId, isStudying) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;

    await supabase
      .from('room_participants')
      .update({
        is_studying: isStudying,
        focus_start_time: isStudying ? new Date().toISOString() : null,
        session_elapsed_seconds: 0,
        session_xp: 0,
      })
      .eq('room_id', roomId)
      .eq('user_id', userId);
  },

  subscribeRoom: (roomId) => {
    if (!isSupabaseConfigured) return () => { };

    const channel = supabase
      .channel(`room-${roomId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages', filter: `room_id=eq.${roomId}` },
        () => {
          void get().fetchRoomDetails(roomId);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'room_participants', filter: `room_id=eq.${roomId}` },
        () => {
          void get().fetchRoomDetails(roomId);
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  },
}));

// Leaderboard Store
interface LeaderboardState {
  leaders: Profile[];
  loading: boolean;
  fetchLeaderboard: () => Promise<void>;
}

export const useLeaderboardStore = create<LeaderboardState>((set) => ({
  leaders: [],
  loading: true,

  fetchLeaderboard: async () => {
    if (!isSupabaseConfigured) {
      set({ loading: false });
      return;
    }

    const { data } = await supabase.from('profiles').select('*').order('xp', { ascending: false }).limit(50);
    set({ leaders: (data as Profile[]) || [], loading: false });
  },
}));

// Feed Store
interface FeedState {
  posts: FeedPost[];
  comments: Record<string, FeedComment[]>;
  loading: boolean;
  fetchPosts: () => Promise<void>;
  createPost: (postType: FeedPost['post_type'], content: string, metadata?: Record<string, unknown>) => Promise<void>;
  toggleLike: (postId: string) => Promise<void>;
  fetchComments: (postId: string) => Promise<void>;
  addComment: (postId: string, content: string) => Promise<void>;
  subscribeRealtime: () => () => void;
}

export const useFeedStore = create<FeedState>((set, get) => ({
  posts: [],
  comments: {},
  loading: true,

  fetchPosts: async () => {
    if (!isSupabaseConfigured) { set({ loading: false }); return; }
    const userId = useAuthStore.getState().user?.id;

    const { data: posts } = await supabase
      .from('feed_posts')
      .select('*, profiles(*)')
      .order('created_at', { ascending: false })
      .limit(50);

    if (!posts) { set({ loading: false }); return; }

    // Fetch like counts + user like status in parallel
    const enriched: FeedPost[] = await Promise.all(
      (posts as FeedPost[]).map(async (post) => {
        const [likesRes, userLikeRes, commentsRes] = await Promise.all([
          supabase.from('feed_likes').select('id', { count: 'exact', head: true }).eq('post_id', post.id),
          userId
            ? supabase.from('feed_likes').select('id').eq('post_id', post.id).eq('user_id', userId).maybeSingle()
            : Promise.resolve({ data: null }),
          supabase.from('feed_comments').select('id', { count: 'exact', head: true }).eq('post_id', post.id),
        ]);
        return {
          ...post,
          like_count: likesRes.count || 0,
          comment_count: commentsRes.count || 0,
          user_has_liked: Boolean(userLikeRes.data),
        };
      })
    );

    set({ posts: enriched, loading: false });
  },

  createPost: async (postType, content, metadata = {}) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    await supabase.from('feed_posts').insert({ user_id: userId, post_type: postType, content, metadata });
    await get().fetchPosts();
  },

  toggleLike: async (postId) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;

    const post = get().posts.find((p) => p.id === postId);
    if (!post) return;

    if (post.user_has_liked) {
      await supabase.from('feed_likes').delete().eq('post_id', postId).eq('user_id', userId);
    } else {
      await supabase.from('feed_likes').insert({ post_id: postId, user_id: userId });
    }

    // Optimistic update
    set((state) => ({
      posts: state.posts.map((p) =>
        p.id === postId
          ? {
            ...p,
            user_has_liked: !p.user_has_liked,
            like_count: (p.like_count || 0) + (p.user_has_liked ? -1 : 1),
          }
          : p
      ),
    }));
  },

  fetchComments: async (postId) => {
    if (!isSupabaseConfigured) return;
    const { data } = await supabase
      .from('feed_comments')
      .select('*, profiles(*)')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });
    set((state) => ({
      comments: { ...state.comments, [postId]: (data as FeedComment[]) || [] },
    }));
  },

  addComment: async (postId, content) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured || !content.trim()) return;
    await supabase.from('feed_comments').insert({ post_id: postId, user_id: userId, content: content.trim() });
    await get().fetchComments(postId);
    // Bump comment count optimistically
    set((state) => ({
      posts: state.posts.map((p) =>
        p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p
      ),
    }));
  },

  subscribeRealtime: () => {
    if (!isSupabaseConfigured) return () => { };

    const channel = supabase
      .channel('feed-realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'feed_posts' }, () => {
        void get().fetchPosts();
      })
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  },
}));

// Admin Store
interface AdminState {
  users: Profile[];
  media: MediaItem[];
  subjects: Subject[];
  metrics: {
    totalUsers: number;
    totalSessions: number;
    totalMinutes: number;
    activeToday: number;
  };
  loading: boolean;
  fetchUsers: () => Promise<void>;
  fetchMedia: () => Promise<void>;
  fetchSubjects: () => Promise<void>;
  fetchMetrics: () => Promise<void>;
  updateUserStats: (userId: string, updates: { xp?: number; streak?: number; total_focus_minutes?: number; role?: string }) => Promise<void>;
  deleteUserData: (userId: string) => Promise<void>;
  addMedia: (item: Pick<MediaItem, 'title' | 'media_type' | 'url' | 'thumbnail_url' | 'duration_seconds' | 'is_default'>) => Promise<void>;
  deleteMedia: (id: string) => Promise<void>;
  addSubject: (subject: Pick<Subject, 'code' | 'exam_type' | 'name' | 'total_chapters'>) => Promise<void>;
  deleteSubject: (id: string) => Promise<void>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  users: [],
  media: [],
  subjects: [],
  metrics: { totalUsers: 0, totalSessions: 0, totalMinutes: 0, activeToday: 0 },
  loading: true,

  fetchUsers: async () => {
    if (!isSupabaseConfigured) { set({ loading: false }); return; }
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    set({ users: (data as Profile[]) || [], loading: false });
  },

  fetchMedia: async () => {
    if (!isSupabaseConfigured) return;
    const { data } = await supabase.from('media_library').select('*').order('created_at', { ascending: false });
    set({ media: (data as MediaItem[]) || [] });
  },

  fetchSubjects: async () => {
    if (!isSupabaseConfigured) return;
    const { data } = await supabase.from('subjects').select('*').order('exam_type').order('name');
    set({ subjects: (data as Subject[]) || [] });
  },

  fetchMetrics: async () => {
    if (!isSupabaseConfigured) return;
    const today = new Date().toISOString().slice(0, 10);

    const [usersRes, sessionsRes, statsRes] = await Promise.all([
      supabase.from('profiles').select('id', { count: 'exact', head: true }),
      supabase.from('study_sessions').select('id', { count: 'exact', head: true }),
      supabase.from('daily_stats').select('minutes_studied, date').gte('date', today),
    ]);

    const totalMinutes = (statsRes.data || []).reduce((s: number, d: { minutes_studied: number }) => s + d.minutes_studied, 0);
    const activeToday = new Set((statsRes.data || []).map((d: { date: string }) => d.date)).size > 0
      ? (statsRes.data || []).length
      : 0;

    set({
      metrics: {
        totalUsers: usersRes.count || 0,
        totalSessions: sessionsRes.count || 0,
        totalMinutes,
        activeToday,
      },
    });
  },

  updateUserStats: async (userId, updates) => {
    if (!isSupabaseConfigured) return;
    try {
      await supabase.rpc('admin_update_user_stats', {
        p_user_id: userId,
        p_xp: updates.xp ?? null,
        p_streak: updates.streak ?? null,
        p_total_focus_minutes: updates.total_focus_minutes ?? null,
        p_role: updates.role ?? null,
      });
      await get().fetchUsers();
    } catch (e) {
      console.error('Admin update user stats failed:', e);
    }
  },

  deleteUserData: async (userId) => {
    if (!isSupabaseConfigured) return;
    try {
      await supabase.rpc('admin_delete_user_data', { p_user_id: userId });
      await get().fetchUsers();
    } catch (e) {
      console.error('Admin delete user data failed:', e);
    }
  },

  addMedia: async (item) => {
    if (!isSupabaseConfigured) return;
    const userId = useAuthStore.getState().user?.id;
    await supabase.from('media_library').insert({ ...item, uploaded_by: userId });
    await get().fetchMedia();
  },

  deleteMedia: async (id) => {
    if (!isSupabaseConfigured) return;
    await supabase.from('media_library').delete().eq('id', id);
    await get().fetchMedia();
  },

  addSubject: async (subject) => {
    if (!isSupabaseConfigured) return;
    await supabase.from('subjects').insert(subject);
    await get().fetchSubjects();
  },

  deleteSubject: async (id) => {
    if (!isSupabaseConfigured) return;
    await supabase.from('subjects').delete().eq('id', id);
    await get().fetchSubjects();
  },
}));

// Media Store (for regular users - read-only media library)
interface MediaState {
  audioItems: MediaItem[];
  videoItems: MediaItem[];
  loading: boolean;
  fetchMedia: () => Promise<void>;
}

export const useMediaStore = create<MediaState>((set) => ({
  audioItems: [],
  videoItems: [],
  loading: true,

  fetchMedia: async () => {
    if (!isSupabaseConfigured) { set({ loading: false }); return; }
    const { data } = await supabase.from('media_library').select('*').order('is_default', { ascending: false }).order('title');
    const items = (data as MediaItem[]) || [];
    set({
      audioItems: items.filter((i) => i.media_type === 'audio'),
      videoItems: items.filter((i) => i.media_type === 'video'),
      loading: false,
    });
  },
}));

// Voice Notes Store
interface VoiceState {
  notes: VoiceNote[];
  loading: boolean;
  recording: boolean;
  fetchNotes: () => Promise<void>;
  addNote: (note: Pick<VoiceNote, 'transcript' | 'audio_url' | 'duration_seconds' | 'session_id'>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
}

export const useVoiceStore = create<VoiceState>((set, get) => ({
  notes: [],
  loading: true,
  recording: false,

  fetchNotes: async () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) { set({ loading: false }); return; }
    const { data } = await supabase
      .from('voice_notes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    set({ notes: (data as VoiceNote[]) || [], loading: false });
  },

  addNote: async (note) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    await supabase.from('voice_notes').insert({ ...note, user_id: userId });
    await get().fetchNotes();
  },

  deleteNote: async (id) => {
    if (!isSupabaseConfigured) return;
    await supabase.from('voice_notes').delete().eq('id', id);
    set((state) => ({ notes: state.notes.filter((n) => n.id !== id) }));
  },
}));

// ─── Daily Targets Store ─────────────────────────────────────
interface DailyTargetsState {
  targets: DailyTarget[];
  loading: boolean;
  fetchTargets: (date?: string) => Promise<void>;
  setTarget: (targetMinutes: number, subjectId?: string | null) => Promise<void>;
  getGlobalTarget: () => DailyTarget | null;
}

export const useDailyTargetsStore = create<DailyTargetsState>((set, get) => ({
  targets: [],
  loading: false,

  fetchTargets: async (date) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    set({ loading: true });
    const targetDate = date || new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from('daily_targets')
      .select('*')
      .eq('user_id', userId)
      .eq('date', targetDate);
    set({ targets: (data as DailyTarget[]) || [], loading: false });
  },

  setTarget: async (targetMinutes, subjectId = null) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    const today = new Date().toISOString().slice(0, 10);
    await supabase.from('daily_targets').upsert(
      {
        user_id: userId,
        date: today,
        target_minutes: targetMinutes,
        subject_id: subjectId,
      },
      { onConflict: 'user_id,date,subject_id' }
    );
    await get().fetchTargets();
  },

  getGlobalTarget: () => {
    return get().targets.find((t) => !t.subject_id) || null;
  },
}));

// ─── Subject Streaks Store ───────────────────────────────────
interface SubjectStreaksState {
  streaks: SubjectStreak[];
  loading: boolean;
  fetchStreaks: () => Promise<void>;
  getStreak: (subjectId: string) => SubjectStreak | undefined;
}

export const useSubjectStreaksStore = create<SubjectStreaksState>((set, get) => ({
  streaks: [],
  loading: false,

  fetchStreaks: async () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    set({ loading: true });
    const { data } = await supabase
      .from('subject_streaks')
      .select('*')
      .eq('user_id', userId);
    set({ streaks: (data as SubjectStreak[]) || [], loading: false });
  },

  getStreak: (subjectId) => {
    return get().streaks.find((s) => s.subject_id === subjectId);
  },
}));

// ─── Recommendations Store ───────────────────────────────────
interface RecommendationsState {
  recommendations: Recommendation[];
  loading: boolean;
  fetchRecommendations: () => Promise<void>;
}

export const useRecommendationsStore = create<RecommendationsState>((set) => ({
  recommendations: [],
  loading: false,

  fetchRecommendations: async () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || !isSupabaseConfigured) return;
    set({ loading: true });
    try {
      const { data } = await supabase.rpc('get_daily_recommendations', {
        p_user_id: userId,
        p_limit: 8,
      });
      set({ recommendations: (data as Recommendation[]) || [], loading: false });
    } catch {
      set({ loading: false });
    }
  },
}));

// ─── Intelligence Helpers (client-side computed) ─────────────

export function computeHealthReports(
  subjects: Subject[],
  progress: SubjectProgress[],
  chapters: Chapter[],
  streaks: SubjectStreak[]
): SubjectHealthReport[] {
  return subjects.map((subject) => {
    const sp = progress.find((p) => p.subject_id === subject.id);
    const subChapters = chapters.filter((c) => c.subject_id === subject.id);
    const streak = streaks.find((s) => s.subject_id === subject.id);

    const totalChapters = Math.max(1, subject.total_chapters);
    const completedChapters = subChapters.filter((c) => c.is_completed).length;
    const completionPct = Math.round((completedChapters / totalChapters) * 100);

    const weakChapters = subChapters
      .filter(
        (c) =>
          (c.is_completed && (c.confidence ?? 0) < 40) ||
          (!c.is_completed && c.total_minutes > 30)
      )
      .map((c) => c.name);

    // Revision frequency: avg revisions per completed chapter
    const totalRevisions = subChapters.reduce((s, c) => s + c.revision_count, 0);
    const revisionFrequency = completedChapters > 0 ? totalRevisions / completedChapters : 0;

    // Health score components (mirrors SQL but recomputed client-side for responsiveness)
    let health = 0;
    // Completion (40pt)
    health += Math.min(40, Math.round((completedChapters / totalChapters) * 40));
    // Revision frequency (20pt)
    health += Math.min(20, Math.round(revisionFrequency * 10));
    // Dummy consistency (20pt) — would need session data; use sp score if available
    health += Math.min(20, sp?.health_score ? Math.round((sp.health_score / 100) * 20) : 0);
    // Confidence average (20pt)
    const completedWithConfidence = subChapters.filter((c) => c.is_completed);
    const avgConfidence =
      completedWithConfidence.length > 0
        ? completedWithConfidence.reduce((s, c) => s + (c.confidence ?? 0), 0) / completedWithConfidence.length
        : 0;
    health += Math.min(20, Math.round((avgConfidence / 100) * 20));

    // Prefer server health_score if available
    const finalHealth = sp?.health_score && sp.health_score > 0 ? sp.health_score : Math.min(100, health);

    // Predicted completion
    const avgDaily = sp?.avg_daily_minutes || 0;
    const remaining = Math.max(0, totalChapters - completedChapters);
    let predictedCompletion: string | null = sp?.predicted_completion_date || null;
    if (!predictedCompletion && avgDaily > 0 && remaining > 0) {
      const days = Math.ceil((remaining * 60) / avgDaily);
      const d = new Date();
      d.setDate(d.getDate() + days);
      predictedCompletion = d.toISOString().slice(0, 10);
    }

    return {
      subjectId: subject.id,
      subjectName: subject.name,
      healthScore: finalHealth,
      completionPct,
      revisionFrequency: Math.round(revisionFrequency * 10) / 10,
      consistency: 0, // filled by server
      weakChapters,
      predictedCompletion,
      currentStreak: streak?.current_streak || 0,
      bestStreak: streak?.best_streak || 0,
    };
  });
}

export function getTopRecommendationsLocal(
  chapters: Chapter[],
  progress: SubjectProgress[],
  subjects: Subject[],
  limit = 5
): Recommendation[] {
  const recs: Recommendation[] = [];

  // 1. Overdue revisions
  const now = new Date().toISOString().slice(0, 10);
  chapters
    .filter((c) => c.is_completed && c.next_revision_date && c.next_revision_date <= now)
    .sort((a, b) => (a.next_revision_date || '').localeCompare(b.next_revision_date || ''))
    .slice(0, limit)
    .forEach((c) => {
      const sub = subjects.find((s) => s.id === c.subject_id);
      const overdueDays = Math.max(0, Math.round((Date.now() - new Date(c.next_revision_date!).getTime()) / 86400000));
      recs.push({
        recommendation_type: 'revision_due',
        subject_id: c.subject_id,
        subject_name: sub?.name || 'Unknown',
        chapter_id: c.id,
        chapter_name: c.name,
        reason: `Revision overdue by ${overdueDays} day${overdueDays !== 1 ? 's' : ''}`,
        priority_score: 90 + Math.min(10, overdueDays),
        estimated_minutes: 30,
      });
    });

  // 2. Weak areas (low confidence)
  chapters
    .filter((c) => c.is_completed && (c.confidence ?? 100) < 40)
    .sort((a, b) => (a.confidence ?? 100) - (b.confidence ?? 100))
    .slice(0, limit)
    .forEach((c) => {
      const sub = subjects.find((s) => s.id === c.subject_id);
      recs.push({
        recommendation_type: 'weak_area',
        subject_id: c.subject_id,
        subject_name: sub?.name || 'Unknown',
        chapter_id: c.id,
        chapter_name: c.name,
        reason: `Low confidence (${c.confidence ?? 0}%)`,
        priority_score: 70 + (40 - Math.min(40, c.confidence ?? 0)),
        estimated_minutes: 45,
      });
    });

  // 3. Continue studying (next incomplete chapter per active subject)
  const activeSubjectIds = progress.filter((p) => p.minutes_studied > 0).map((p) => p.subject_id);
  activeSubjectIds.forEach((sid) => {
    const next = chapters
      .filter((c) => c.subject_id === sid && !c.is_completed)
      .sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))[0];
    if (next) {
      const sub = subjects.find((s) => s.id === sid);
      recs.push({
        recommendation_type: 'continue',
        subject_id: sid,
        subject_name: sub?.name || 'Unknown',
        chapter_id: next.id,
        chapter_name: next.name,
        reason: 'Next chapter to complete',
        priority_score: 50 + (sub?.priority === 'high' ? 20 : sub?.priority === 'medium' ? 10 : 0),
        estimated_minutes: 60,
      });
    }
  });

  // 4. Neglected subjects (not studied in 3+ days)
  progress
    .filter((p) => {
      if (p.minutes_studied === 0) return false;
      if (!p.last_studied_at) return true;
      const days = Math.round((Date.now() - new Date(p.last_studied_at).getTime()) / 86400000);
      return days >= 3;
    })
    .forEach((p) => {
      const sub = subjects.find((s) => s.id === p.subject_id);
      const days = p.last_studied_at
        ? Math.round((Date.now() - new Date(p.last_studied_at).getTime()) / 86400000)
        : 99;
      recs.push({
        recommendation_type: 'neglected',
        subject_id: p.subject_id,
        subject_name: sub?.name || 'Unknown',
        chapter_id: null,
        chapter_name: null,
        reason: `Not studied for ${days} day${days !== 1 ? 's' : ''}`,
        priority_score: 40 + Math.min(30, days),
        estimated_minutes: 45,
      });
    });

  // Sort by priority descending & deduplicate
  return recs
    .sort((a, b) => b.priority_score - a.priority_score)
    .slice(0, limit);
}
